import Button from '@mui/material/Button';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';

/**
 * MonthYearPickerAction
 *
 * Button-triggered month/year selector using the default MUI calendar.
 *
 * VALUE CONTRACT
 * - Emits { year: number, month: number }
 *
 * @param {Object} props
 * @param {number} props.year - Selected year (YYYY).
 * @param {number} props.month - Selected month (0–11).
 * @param {(next: { year: number, month: number }) => void} props.onChange
 * @param {number} [props.minYear] - Earliest selectable year.
 * @param {number} [props.minMonth] - Earliest selectable month (0–11) for minYear.
 *
 * @returns {React.ReactElement}
 */
interface MonthYearPickerActionProps {
  year: number;
  /** 0-11, matching `Date#getMonth`. */
  month: number;
  onChange: (next: { year: number; month: number }) => void;
  minYear?: number;
  minMonth?: number;
}

export default function MonthYearPickerAction({
  year,
  month,
  onChange,
  minYear,
  minMonth,
}: MonthYearPickerActionProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);

  const value = dayjs().year(year).month(month).startOf('month');

  const minDate =
    typeof minYear === 'number'
      ? dayjs()
          .year(minYear)
          .month(minMonth ?? 0)
          .startOf('month')
      : undefined;

  const viewStyle = {
    fontSize: '0.875rem',
    borderRadius: '8px',
    '&.Mui-selected': {
      backgroundColor: (theme) => `${theme.palette.primary.light} !important`,
    },
  };

  return (
    <>
      <Button
        ref={buttonRef}
        variant='outlined'
        onClick={() => setOpen(true)}
        sx={{
          border: '2px solid',
          borderColor: 'grey.300',
          borderRadius: '8px',
          color: 'text.primary',
        }}
      >
        {value.format('MMMM, YYYY')}
      </Button>

      <DatePicker
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        openTo='month'
        onChange={(next) => {
          if (!next) return;
          onChange({
            year: next.year(),
            month: next.month(),
          });
          setOpen(false);
        }}
        views={['year', 'month']}
        minDate={minDate}
        format='MMM YYYY'
        slotProps={{
          textField: { sx: { display: 'none' } },

          popper: {
            anchorEl: buttonRef.current,
            placement: 'bottom-end',
            sx: {
              '& .MuiDateCalendar-root': {
                height: 'auto',
              },
            },
          },

          monthButton: { sx: viewStyle },
          yearButton: { sx: viewStyle },
        }}
      />
    </>
  );
}
